import { PlatformChartGateActivationDto } from '@data/_generated-api/platform-api/models/platform-chart-gate-activation-dto';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import {
  HD_LOCKS_KEYS_LOCKS,
  HdLocksKeysPeriodId,
  HdLocksKeysSearchFilter,
  HdLocksKeysSearchSort,
  hdLocksKeysKeyGate,
} from './hd-locks-keys-metadata';

/**
 * Planeten van het incarnatiekruis (zon, aarde) en het levenskruis (noord- en
 * zuidknoop), als planetId van de planeetconfig.
 */
export const HD_LOCKS_KEYS_CROSS_PLANET_IDS: ReadonlySet<number> = new Set([0, 1, 3, 4]);

/**
 * Welke lock/key-relaties de zoektocht vanuit de actieve chart zoekt (spec
 * 2026-10-21-poortactivatie-zoektochten §4.6): charts met de key voor mijn
 * locks, charts met het slot voor mijn keys, of beide.
 */
export type HdLocksKeysSearchKind = 'keysForMyLocks' | 'locksForMyKeys' | 'both';

export const HD_LOCKS_KEYS_SEARCH_KINDS: readonly HdLocksKeysSearchKind[] = ['keysForMyLocks', 'locksForMyKeys', 'both'];

/**
 * Eén gezochte relatie: de actieve chart activeert `myGate`, een treffer moet
 * `theirGate` activeren. `theirKey` = de ander draagt de key voor mijn lock,
 * `theirLock` = de ander draagt het slot dat mijn key opent.
 */
export interface HdLocksKeysSearchCandidate {
  readonly lockGate: number;
  readonly keyGate: number;
  readonly direction: 'theirKey' | 'theirLock';
  readonly myGate: number;
  readonly theirGate: number;
}

/** Een gevonden relatie met de activaties van de treffer op zijn kant (planeet, zijde, lijn). */
export interface HdLocksKeysSearchRelation extends HdLocksKeysSearchCandidate {
  readonly activations: readonly PlatformChartGateActivationDto[];
}

export interface HdLocksKeysSearchMatch {
  readonly item: PlatformChartListItemDto;
  readonly relations: readonly HdLocksKeysSearchRelation[];
  /** Relaties in beide richtingen: de ander opent mijn slot én mijn key opent het zijne. */
  readonly mutual: boolean;
}

/**
 * Bouwt de gezochte relaties uit de poorten van de actieve chart in de gekozen
 * periode. In een lock-fase (key = de lock-poort zelf) kan één poort beide
 * richtingen dragen; dat is dan ook een wederzijdse relatie.
 */
export function buildHdLocksKeysSearchCandidates(
  kind: HdLocksKeysSearchKind,
  periodId: HdLocksKeysPeriodId,
  myGates: Iterable<number>,
): HdLocksKeysSearchCandidate[] {
  const mine = new Set(myGates);
  const candidates: HdLocksKeysSearchCandidate[] = [];
  for (const lock of HD_LOCKS_KEYS_LOCKS) {
    const keyGate = hdLocksKeysKeyGate(lock.gate, periodId);
    if (kind !== 'locksForMyKeys' && mine.has(lock.gate)) {
      candidates.push({ lockGate: lock.gate, keyGate, direction: 'theirKey', myGate: lock.gate, theirGate: keyGate });
    }
    if (kind !== 'keysForMyLocks' && mine.has(keyGate)) {
      candidates.push({ lockGate: lock.gate, keyGate, direction: 'theirLock', myGate: keyGate, theirGate: lock.gate });
    }
  }
  return candidates;
}

/** Poorten waarop de bibliotheek doorzocht wordt (Of): de kant van de ander. */
export function hdLocksKeysSearchGates(candidates: readonly HdLocksKeysSearchCandidate[]): number[] {
  return [...new Set(candidates.map(candidate => candidate.theirGate))];
}

/**
 * Verklaart de treffers van de poortzoektocht: per chart de relaties die hij
 * invult, met zijn activaties op zijn kant. Sortering: meeste relaties eerst,
 * dan op naam.
 */
export function collectHdLocksKeysSearchMatches(
  items: readonly PlatformChartListItemDto[],
  candidates: readonly HdLocksKeysSearchCandidate[],
): HdLocksKeysSearchMatch[] {
  const matches: HdLocksKeysSearchMatch[] = [];
  for (const item of items) {
    const activations = item.hdGateActivations ?? [];
    // Eerst de keys die de ander voor mijn locks draagt, dan de locks voor mijn keys:
    // zo staan de blauwe en de gouden kadertjes in een rij telkens samen.
    const relations = candidates
      .map(candidate => ({
        ...candidate,
        activations: activations.filter(activation => activation.gate === candidate.theirGate),
      }))
      .filter(relation => relation.activations.length > 0)
      .sort((a, b) => Number(a.direction === 'theirLock') - Number(b.direction === 'theirLock'));
    if (relations.length === 0) continue;
    matches.push({
      item,
      relations,
      mutual: relations.some(relation => relation.direction === 'theirKey')
        && relations.some(relation => relation.direction === 'theirLock'),
    });
  }
  return arrangeHdLocksKeysSearchMatches(matches, 'all', 'relations');
}

/**
 * Weergave van de treffers volgens de keuzes van de gebruiker (client-side, geen
 * nieuwe zoektocht):
 * - `cross` houdt per treffer enkel de relaties waarbij de ander zijn key/lock met
 *   zon, aarde, noord- of zuidknoop activeert; treffers zonder zo'n relatie vallen
 *   weg en "wederzijds" wordt herberekend;
 * - `relations` sorteert op aantal relaties en dan op naam, `name` enkel op naam.
 */
export function arrangeHdLocksKeysSearchMatches(
  matches: readonly HdLocksKeysSearchMatch[],
  filter: HdLocksKeysSearchFilter,
  sort: HdLocksKeysSearchSort,
): HdLocksKeysSearchMatch[] {
  const filtered = filter === 'cross'
    ? matches
      .map(match => {
        const relations = match.relations.filter(relation =>
          relation.activations.some(activation => HD_LOCKS_KEYS_CROSS_PLANET_IDS.has(activation.planetId ?? -1)));
        return {
          ...match,
          relations,
          mutual: relations.some(relation => relation.direction === 'theirKey')
            && relations.some(relation => relation.direction === 'theirLock'),
        };
      })
      .filter(match => match.relations.length > 0)
    : [...matches];
  const byName = (a: HdLocksKeysSearchMatch, b: HdLocksKeysSearchMatch) =>
    (a.item.name ?? '').localeCompare(b.item.name ?? '');
  return filtered.sort(sort === 'name'
    ? byName
    : (a, b) => b.relations.length - a.relations.length || byName(a, b));
}
